import React, {useState} from "react";
import '../../css/App.css';
import logo from '../../assets/farma-logo.png';
import useAuth from '../../hooks/useAuth';
import MenuLink from "./MenuLink";
import {hasPermission, hasAnyPermission, PERMISSIONS as P} from "../../auth/permissions";
import {normalizeRoles, ROLES} from "../../auth/roles";

const LayoutSidebarLeft = () => {
  const {auth} = useAuth();
  const permissions = auth?.permissions || [];
  const roles = normalizeRoles(auth?.roles);
  const isMinion = roles.includes(ROLES.MINION);
  const hasLinkedEmployee = auth?.linkedEmployee === true || auth?.employeeId != null;
  const can = permission => hasPermission(permissions, permission);
  const any = values => hasAnyPermission(permissions, values);

  const [isNavigationVisible, setNavigationVisible] = useState(true);
  const [isEmployeeVisible, setEmployeeVisible] = useState(true);
  const [isResourcesVisible, setResourcesVisible] = useState(true);
  const [isDocumentsVisible, setDocumentsVisible] = useState(true);
  const [isAdministrationVisible, setAdministrationVisible] = useState(true);
  const [isAppSettingsVisible, setAppSettingsVisible] = useState(false);
  const [isCompanySettingsVisible, setCompanySettingsVisible] = useState(false);
  const [isDictionariesVisible, setDictionariesVisible] = useState(false);
  const [isOfficeSettingsVisible, setOfficeSettingsVisible] = useState(false);

  const SectionHeading = ({label, open, onClick}) => (
    <div className={`sb-sidenav-menu-heading sidebar-section-toggle ${open ? "open" : ""}`} onClick={onClick} role="button" tabIndex={0}>
      <span>{label}</span>
      <i className={`fas fa-chevron-down sidebar-section-chevron ${open ? "open" : ""}`}></i>
    </div>
  );

  const SubsectionHeading = ({label, open, onClick}) => (
    <div className={`sb-sidenav-menu-heading accordion sidebar-subsection-toggle ${open ? "open" : ""}`} onClick={onClick} role="button" tabIndex={0}>
      <span>{label}</span>
      <i className={`fas fa-chevron-down sidebar-section-chevron ${open ? "open" : ""}`}></i>
    </div>
  );

  const LinkIf = ({permission, permissions: requiredPermissions, path, label}) => {
    const visible = permission ? can(permission) : any(requiredPermissions || []);
    return visible ? <MenuLink path={path} label={label}/> : null;
  };

  const canSeeAdministration = any([
    P.USERS_VIEW, P.USERS_MANAGE, P.ROLES_VIEW, P.ROLES_MANAGE, P.PERMISSIONS_MANAGE,
    P.COMPANY_SETTINGS_VIEW, P.COMPANY_SETTINGS_MANAGE, P.DICTIONARIES_MANAGE, P.COMMUNICATION_ADMIN,
    P.CONTRACTS_MANAGE
  ]);

  return (
    <div id="layoutSidenav_nav">
      <nav className="sb-sidenav accordion sb-sidenav-dark" id="sidenavAccordion">
        <div className="sb-sidenav-menu">
          <div className="nav">
            <SectionHeading label="Nawigacja" open={isNavigationVisible} onClick={() => setNavigationVisible(value => !value)}/>
            <div className={`accordion-content sidebar-main-section ${isNavigationVisible ? "expanded" : ""}`}>
              <LinkIf permission={P.DASHBOARD_VIEW} path="/app/dashboard" label="Tablica"/>
              {!isMinion && <MenuLink path="/app/account" label="Moje konto"/>}
              <LinkIf permission={P.SCHEDULES_VIEW} path="/app/scheduler" label="Grafik"/>
              <LinkIf permission={P.SCHEDULE_REQUESTS_VIEW} path="/app/schedule-requests" label="Wnioski do grafiku"/>
              <LinkIf permission={P.SCHEDULE_NOTIFICATIONS_STATUS} path="/app/schedule-notifications" label="Powiadomienia grafików"/>
              <LinkIf permission={P.EMPLOYEE_CONTACT_REQUESTS_MANAGE} path="/app/employee-contact-requests" label="Wiadomości pracowników"/>
              <LinkIf permission={P.HR_APPOINTMENTS_MANAGE} path="/app/hr-appointments" label="Spotkania HR"/>
              <LinkIf permission={P.VACATIONS_VIEW} path="/app/department-vacations" label="Urlopy działów"/>
              <LinkIf permission={P.REPORTS_OPERATIONAL_VIEW} path="/app/schedule-reports" label="Raporty grafików"/>
              <LinkIf permission={P.RCP_EVENTS_VIEW} path="/app/rcp-events" label="Rejestr RCP"/>
              <LinkIf permission={P.RCP_TERMINALS_VIEW} path="/app/rcp-terminals" label="Terminale RCP"/>
            </div>

            {hasLinkedEmployee && any([P.SELF_PROFILE_VIEW, P.SELF_SCHEDULE_VIEW, P.SELF_NOTIFICATIONS, P.SELF_RESOURCES]) && (
              <>
                <SectionHeading label="Pracownik" open={isEmployeeVisible} onClick={() => setEmployeeVisible(value => !value)}/>
                <div className={`accordion-content sidebar-main-section ${isEmployeeVisible ? "expanded" : ""}`}>
                  <LinkIf permission={P.SELF_RESOURCES} path="/app/employee-panel" label="Panel pracownika"/>
                  <LinkIf permission={P.SELF_SCHEDULE_VIEW} path="/app/my-schedule" label="Mój grafik"/>
                  <LinkIf permission={P.SELF_NOTIFICATIONS} path="/app/notifications" label="Powiadomienia"/>
                  <LinkIf permission={P.SELF_PROFILE_VIEW} path="/app/employee-profile" label="Moje dane"/>
                </div>
              </>
            )}

            {any([
              P.EMPLOYEES_VIEW_BASIC, P.EMPLOYEES_EDIT_BASIC, P.EMPLOYEES_EDIT_HR,
              P.RESOURCE_PLANS_VIEW, P.RESOURCE_PLANS_MANAGE, P.AVAILABILITY_VIEW, P.AVAILABILITY_MANAGE,
              P.CONTRACTS_VIEW, P.CONTRACTS_MANAGE, P.TRAININGS_VIEW, P.TRAININGS_MANAGE,
              P.TRAINING_TESTS_MANAGE, P.DICTIONARIES_MANAGE
            ]) && (
              <>
                <SectionHeading label="Zasoby" open={isResourcesVisible} onClick={() => setResourcesVisible(value => !value)}/>
                <div className={`accordion-content sidebar-main-section ${isResourcesVisible ? "expanded" : ""}`}>
                  <LinkIf permission={P.EMPLOYEES_VIEW_BASIC} path="/app/employees" label="Pracownicy"/>
                  <LinkIf permissions={[P.EMPLOYEES_VIEW_HR, P.EMPLOYEES_CREATE]} path="/app/recruitment-waiting-room" label="Poczekalnia rekrutacyjna"/>
                  <LinkIf permission={P.AVAILABILITY_VIEW} path="/app/employee-availability-schedule" label="Grafik dostępności"/>
                  <LinkIf permission={P.RESOURCE_PLANS_VIEW} path="/app/resource-plan" label="Plany zapotrzebowania"/>
                  <LinkIf permission={P.DICTIONARIES_MANAGE} path="/app/attractions" label="Atrakcje"/>
                  <LinkIf permission={P.DICTIONARIES_MANAGE} path="/app/business-partner" label="Kontrahenci"/>
                  <LinkIf permission={P.DICTIONARIES_MANAGE} path="/app/time-cards" label="Karty RFID"/>
                </div>

                <SectionHeading label="Dokumenty" open={isDocumentsVisible} onClick={() => setDocumentsVisible(value => !value)}/>
                <div className={`accordion-content sidebar-main-section ${isDocumentsVisible ? "expanded" : ""}`}>
                  <LinkIf permission={P.CONTRACTS_VIEW} path="/app/contract" label="Umowy"/>
                  <LinkIf permission={P.TRAINING_TESTS_MANAGE} path="/app/trainingx" label="Testy szkoleniowe"/>
                  <LinkIf permission={P.EMPLOYEE_RESOURCES_MANAGE} path="/app/employee-resources" label="Materiały dla pracowników"/>
                  <LinkIf permission={P.CONTRACTS_MANAGE} path="/app/contract-templates" label="Szablony treści umów"/>
                  <LinkIf permissions={[P.TRAININGS_VIEW, P.TRAININGS_MANAGE]} path="/app/training" label="Definicje szkoleń"/>
                </div>
              </>
            )}

            {canSeeAdministration && (
              <>
                <SectionHeading label="Administracja" open={isAdministrationVisible} onClick={() => setAdministrationVisible(value => !value)}/>
                <div className={`accordion-content sidebar-main-section ${isAdministrationVisible ? "expanded" : ""}`}>
                  <LinkIf permissions={[P.USERS_VIEW, P.USERS_MANAGE, P.PERMISSIONS_MANAGE]} path="/app/users" label="Użytkownicy aplikacji"/>
                  <LinkIf permissions={[P.ROLES_VIEW, P.ROLES_MANAGE, P.PERMISSIONS_MANAGE]} path="/app/roles" label="Definicje ról"/>
                  {any([P.COMPANY_SETTINGS_VIEW, P.COMPANY_SETTINGS_MANAGE, P.COMMUNICATION_ADMIN]) && (
                    <>
                      <SubsectionHeading label="Ustawienia aplikacji" open={isAppSettingsVisible} onClick={() => setAppSettingsVisible(value => !value)}/>
                      <div className={`accordion-content ${isAppSettingsVisible ? "expanded" : ""}`}>
                        <LinkIf permissions={[P.COMPANY_SETTINGS_VIEW, P.COMPANY_SETTINGS_MANAGE]} path="/app/company" label="Firma"/>
                        <LinkIf permissions={[P.COMPANY_SETTINGS_VIEW, P.COMPANY_SETTINGS_MANAGE]} path="/app/settings" label="Ustawienia globalne"/>
                        <LinkIf permissions={[P.COMPANY_SETTINGS_MANAGE, P.COMMUNICATION_ADMIN]} path="/app/mail-configuration" label="Poczta e-mail"/>
                        <LinkIf permissions={[P.COMPANY_SETTINGS_MANAGE, P.COMMUNICATION_ADMIN]} path="/app/sms-configuration" label="SMS"/>
                      </div>
                    </>
                  )}

                  {any([P.DICTIONARIES_MANAGE, P.CONTRACTS_MANAGE]) && (
                    <>
                      <SubsectionHeading label="Ustawienia firmy" open={isCompanySettingsVisible} onClick={() => setCompanySettingsVisible(value => !value)}/>
                      <div className={`accordion-content ${isCompanySettingsVisible ? "expanded" : ""}`}>
                        <LinkIf permission={P.DICTIONARIES_MANAGE} path="/app/position" label="Stanowiska"/>
                        <LinkIf permission={P.DICTIONARIES_MANAGE} path="/app/position-type" label="Kategorie zatrudnienia"/>
                        <LinkIf permission={P.DICTIONARIES_MANAGE} path="/app/department" label="Działy firmy"/>
                        <LinkIf permission={P.CONTRACTS_MANAGE} path="/app/document-numbering-pattern" label="Wzorce numeracji umów"/>
                        <LinkIf permission={P.DICTIONARIES_MANAGE} path="/app/document-type" label="Typy dokumentów"/>
                        <LinkIf permission={P.DICTIONARIES_MANAGE} path="/app/user-types" label="Typy użytkowników"/>
                        <LinkIf permission={P.DICTIONARIES_MANAGE} path="/app/zones" label="Strefy parku"/>
                        <LinkIf permission={P.DICTIONARIES_MANAGE} path="/app/locations" label="Lokalizacje"/>
                      </div>
                    </>
                  )}

                  {can(P.DICTIONARIES_MANAGE) && (
                    <>
                      <SubsectionHeading label="Słowniki" open={isDictionariesVisible} onClick={() => setDictionariesVisible(value => !value)}/>
                      <div className={`accordion-content ${isDictionariesVisible ? "expanded" : ""}`}>
                        <MenuLink path="/app/gender" label="Płeć"/>
                        <MenuLink path="/app/hiring-stage" label="Etapy zatrudnienia"/>
                        <MenuLink path="/app/commendation" label="Typy pochwał"/>
                        <MenuLink path="/app/infraction" label="Typy przewinień"/>
                        <MenuLink path="/app/absence-type" label="Typy nieobecności"/>
                        <MenuLink path="/app/training" label="Typy szkoleń"/>
                        <MenuLink path="/app/soft-skill" label="Typy umiejętności"/>
                        <MenuLink path="/app/medical-examination-type" label="Typy badań lekarskich"/>
                        <MenuLink path="/app/certification-type" label="Typy certyfikatów"/>
                        <MenuLink path="/app/business-trip-type" label="Typy wyjazdów służbowych"/>
                        <MenuLink path="/app/identity-card-type" label="Typy dokumentów tożsamości"/>
                        <MenuLink path="/app/contract-type" label="Typy umów"/>
                        <MenuLink path="/app/wage-types" label="Typy stawek"/>
                        <MenuLink path="/app/working-time" label="Wymiary czasu pracy"/>
                        <MenuLink path="/app/non-working-days" label="Dni wolne od pracy"/>
                      </div>

                      <SubsectionHeading label="Ustawienia urzędowe" open={isOfficeSettingsVisible} onClick={() => setOfficeSettingsVisible(value => !value)}/>
                      <div className={`accordion-content ${isOfficeSettingsVisible ? "expanded" : ""}`}>
                        <MenuLink path="/app/country" label="Kraje"/>
                        <MenuLink path="/app/voivodeship" label="Województwa"/>
                        <MenuLink path="/app/tax-office" label="Urzędy skarbowe"/>
                        <MenuLink path="/app/nfz-branch" label="Oddziały NFZ"/>
                        <MenuLink path="/app/legal-entity-type" label="Typy podmiotów prawnych"/>
                        <MenuLink path="/app/zus-basis" label="Podstawy ZUS"/>
                        <MenuLink path="/app/zus-rate" label="Wysokości składek ZUS"/>
                        <MenuLink path="/app/zus-accident-branch" label="Branże wypadkowe ZUS"/>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}

            <LinkIf permission={P.KNOWLEDGE_BASE_VIEW} path="/app/knowledge-base" label="Baza wiedzy"/>
            <img src={logo} className="App-logo" alt="logo"/>
            <div className="d-flex justify-content-center align-items-center">Wersja 1.202609102356</div>
          </div>
        </div>
        <div className="sb-sidenav-footer">
          <div className="small">Zalogowany jako {auth.user}</div>
        </div>
      </nav>
    </div>
  );
};

export default LayoutSidebarLeft;



